'use client';

/**
 * Recently Used Tools Component
 * Shows tools the user has visited recently, stored in localStorage
 */

import { useState, useEffect } from 'react';
import Link from 'next/link';

// Tool metadata for display
const TOOL_METADATA: Record<string, { title: string; icon: string }> = {
  '/splitcause': { title: 'Split Cause (RCA)', icon: '🔍' },
  '/tools/archimate': { title: 'ArchiMate Modeler', icon: '🏛️' },
  '/tools/wardley-map-builder': { title: 'Wardley Map Builder', icon: '🗺️' },
  '/tools/foundry': { title: 'Foundry', icon: '🏭' },
  '/tools/gdpr-compliance': { title: 'GDPR Compliance', icon: '🔒' },
  '/tools/soc2-readiness': { title: 'SOC 2 Readiness', icon: '✅' },
  '/tools/hipaa-compliance': { title: 'HIPAA Compliance', icon: '🏥' },
  '/tools/iso27001-gap-analysis': { title: 'ISO 27001 Gap Analysis', icon: '📋' },
  '/tools/nist-csf-assessment': { title: 'NIST CSF Assessment', icon: '🛡️' },
  '/tools/pci-dss-checker': { title: 'PCI DSS Checker', icon: '💳' },
  '/tools/dora-compliance': { title: 'DORA Compliance', icon: '🏦' },
  '/tools/nis2-readiness': { title: 'NIS2 Readiness', icon: '🇪🇺' },
  '/tools/ccpa-compliance': { title: 'CCPA/CPRA Compliance', icon: '🌴' },
  '/tools/eu-ai-act-compliance': { title: 'EU AI Act Compliance', icon: '🤖' },
  '/tools/vendor-risk-assessment': { title: 'Vendor Risk Assessment', icon: '🤝' },
  '/tools/wcag-compliance': { title: 'WCAG 2.2 Compliance', icon: '♿' },
  '/tools/ai-ethics-assessment': { title: 'AI Ethics Assessment', icon: '⚖️' },
  '/tools/business-continuity': { title: 'Business Continuity', icon: '🔄' },
  '/tools/incident-response': { title: 'Incident Response', icon: '🚨' },
  '/tools/license-compliance-checker': { title: 'License Compliance Checker', icon: '📜' },
  '/tools/security-assessment-checklist': { title: 'Security Assessment', icon: '🔐' },
  '/tools/tech-stack-risk-radar': { title: 'Tech Stack Risk Radar', icon: '📡' },
  '/tools/database-migration-risk-assessor': { title: 'Database Migration Risk', icon: '🗄️' },
  '/tools/architecture-calculator': { title: 'Architecture Calculator', icon: '🧮' },
  '/tools/architecture-maturity-assessment': { title: 'Architecture Maturity', icon: '📈' },
  '/tools/architecture-scenario-planner': { title: 'Architecture Scenario Planner', icon: '🎯' },
  '/tools/microservices-dependency-mapper': { title: 'Microservices Dependency Mapper', icon: '🕸️' },
  '/tools/build-vs-buy-matrix': { title: 'Build vs Buy Matrix', icon: '⚖️' },
  '/tools/api-contract': { title: 'API Contract Designer', icon: '📄' },
  '/tools/system-design': { title: 'System Design Canvas', icon: '🎨' },
  '/tools/cloud-cost-estimator': { title: 'Cloud Cost Estimator', icon: '💰' },
  '/tools/kubernetes-cost-optimizer': { title: 'Kubernetes Cost Optimizer', icon: '☸️' },
  '/tools/connectivity-tester': { title: 'Connectivity Tester', icon: '📡' },
  '/outages': { title: 'Cloud Outage Tracker', icon: '🔴' },
  '/tools/api-rate-limit-planner': { title: 'API Rate Limit Planner', icon: '🚦' },
  '/tools/tech-stack-decision': { title: 'Tech Stack Decision', icon: '🤖' },
  '/tools/tech-debt-prioritizer': { title: 'Tech Debt Prioritizer', icon: '📊' },
  '/tools/tech-debt-paydown-simulator': { title: 'Tech Debt Paydown Simulator', icon: '💸' },
  '/tools/codebase-health-analyzer': { title: 'Codebase Health Analyzer', icon: '🩺' },
  '/tools/cicd-maturity-grader': { title: 'CI/CD Maturity Grader', icon: '🚀' },
  '/tools/headcount-forecasting': { title: 'Headcount Forecasting', icon: '📈' },
  '/tools/team-scaling-calculator': { title: 'Team Scaling Calculator', icon: '👥' },
  '/tools/engineering-org-design': { title: 'Org Design Planner', icon: '🏢' },
  '/tools/salary-band-calculator': { title: 'Salary Band Calculator', icon: '💵' },
  '/tools/interview-question-bank': { title: 'Interview Question Bank', icon: '❓' },
  '/tools/cto-rating-framework': { title: 'IT Leader Rating Framework', icon: '⭐' },
  '/tools/engineering-ladder': { title: 'Engineering Ladder Builder', icon: '🪜' },
  '/tools/team-topology': { title: 'Team Topology Designer', icon: '🔲' },
  '/tools/one-on-one': { title: '1:1 Meeting Assistant', icon: '💬' },
  '/tools/sla-generator': { title: 'SLA Generator', icon: '📝' },
  '/tools/incident-postmortem': { title: 'Incident Post-Mortem', icon: '🔬' },
  '/tools/incident-severity-classifier': { title: 'Incident Severity Classifier', icon: '🚦' },
  '/tools/oncall-rotation-optimizer': { title: 'On-Call Rotation Optimizer', icon: '📅' },
  '/tools/engineering-roi-calculator': { title: 'Engineering ROI Calculator', icon: '📊' },
  '/tools/rfp-generator': { title: 'RFP Generator', icon: '📋' },
  '/tools/sprint-capacity-planner': { title: 'Sprint Capacity Planner', icon: '🏃' },
  '/tools/engineering-metrics-dashboard': { title: 'Engineering Metrics Dashboard', icon: '📈' },
};

const STORAGE_KEY = 'recentlyUsedTools';
const MAX_RECENT = 6;

export interface RecentTool {
  path: string;
  timestamp: number;
}

/**
 * Get recently used tools from localStorage
 */
export function getRecentTools(): RecentTool[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Track a tool usage (call this from tool pages)
 */
export function trackToolUsage(path: string): void {
  if (typeof window === 'undefined') return;
  try {
    const recent = getRecentTools();
    // Remove existing entry for this path
    const filtered = recent.filter(t => t.path !== path);
    // Add to front
    const updated = [{ path, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore storage errors
  }
}

export default function RecentlyUsedTools() {
  const [recentTools, setRecentTools] = useState<RecentTool[]>(() => {
    if (typeof window === 'undefined') return [];
    return getRecentTools();
  });
  const [mounted, setMounted] = useState(false);

   
  useEffect(() => {
    setMounted(true);
  }, []);
   

  // Don't render on server or if no recent tools
  if (!mounted || recentTools.length === 0) {
    return null;
  }

  return (
    <div className="mb-12 p-6 bg-cream/5 dark:bg-surface rounded-lg border border-border">
      <h2 className="text-lg font-bold text-light-text dark:text-foreground mb-4 flex items-center gap-2">
        <span>🕐</span> Recently Used
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {recentTools.map(tool => {
          const metadata = TOOL_METADATA[tool.path];
          if (!metadata) return null;

          return (
            <Link
              key={tool.path}
              href={tool.path}
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border-muted hover:border-border hover:bg-accent-muted transition-colors text-center"
            >
              <span className="text-2xl">{metadata.icon}</span>
              <span className="text-sm text-light-text dark:text-foreground font-medium line-clamp-2">
                {metadata.title}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
